import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api"

//
// 🔑 LOGIN
//
export const adminLogin = createAsyncThunk(
    "admin/login",
    async (data, { rejectWithValue }) => {
        try {
            const res = await api.post("/admin/admin-login", data);
            return res.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || err.message
            );
        }
    }
);

//
// 📃 GET ADMINS
//
export const getAllAdmins = createAsyncThunk(
    "admin/getAll",
    async (_, { rejectWithValue }) => {
        try {
            const res = await api.get("/admin/admins");
            return res.data.admins;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || err.message
            );
        }
    }
);

//
// ✏️ UPDATE ADMIN
//
export const updateAdmin = createAsyncThunk(
    "admin/update",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const res = await api.put(`/admin/admin/update/${id}`, data);
            return res.data.admin;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || err.message
            );
        }
    }
);

//
// 🗑️ DELETE ADMIN
//
export const deleteAdmin = createAsyncThunk(
    "admin/delete",
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/admin/admin/delete/${id}`);
            return id;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || err.message
            );
        }
    }
);

//
// ✅ APPROVE KYC
//
export const approveKYC = createAsyncThunk(
    "admin/approveKYC",
    async (id, { rejectWithValue }) => {
        try {
            await api.put(`/admin/approve/kyc-request/${id}`);
            return id;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || err.message
            );
        }
    }
);

//
// ❌ REJECT KYC
//
export const rejectKYC = createAsyncThunk(
    "admin/rejectKYC",
    async (id, { rejectWithValue }) => {
        try {
            await api.put(`/admin/reject/kyc-request/${id}`);
            return id;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || err.message
            );
        }
    }
);

//
// 🧠 INITIAL STATE
//
const initialState = {
    user: JSON.parse(localStorage.getItem("adminUser")) || null,
    admins: [],
    loading: false,
    error: null
};

//
// 🧠 SLICE
//
const adminSlice = createSlice({
    name: "admin",
    initialState,
    reducers: {
        setUser: (state, action) => {
            const payload = action.payload;

            const userData = {
                ...(payload.user || payload),
                token: payload.token || payload?.user?.token
            };

            state.user = userData;

            if (userData?.token) {
                localStorage.setItem("adminUser", JSON.stringify(userData));
            }
        },
        logout: (state) => {
            state.user = null;
            localStorage.removeItem("adminUser");
        }
    },
    extraReducers: (builder) => {
        builder

            // 🔑 LOGIN
            .addCase(adminLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(adminLogin.fulfilled, (state, action) => {
                state.loading = false;

                const userData = {
                    ...action.payload.user,
                    token: action.payload.token
                };

                state.user = userData;

                localStorage.setItem("adminUser", JSON.stringify(userData));
            })
            .addCase(adminLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // 📃 GET ADMINS
            .addCase(getAllAdmins.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllAdmins.fulfilled, (state, action) => {
                state.loading = false;
                state.admins = action.payload;
            })
            .addCase(getAllAdmins.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // ✏️ UPDATE
            .addCase(updateAdmin.fulfilled, (state, action) => {
                const index = state.admins.findIndex(
                    (a) => a._id === action.payload._id
                );
                if (index !== -1) {
                    state.admins[index] = action.payload;
                }
            })

            // 🗑️ DELETE
            .addCase(deleteAdmin.fulfilled, (state, action) => {
                state.admins = state.admins.filter(
                    (a) => a._id !== action.payload
                );
            })

            // ✅ KYC APPROVE
            .addCase(approveKYC.fulfilled, (state, action) => {
                const user = state.admins.find(
                    (a) => a._id === action.payload
                );
                if (user) user.isApproved = true;
            })

            // ❌ KYC REJECT
            .addCase(rejectKYC.fulfilled, (state, action) => {
                const user = state.admins.find(
                    (a) => a._id === action.payload
                );
                if (user) user.isApproved = false;
            });
    }
});

export const { logout, setUser } = adminSlice.actions;
export default adminSlice.reducer;